import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, ChevronLeft, Check, AlertCircle, Anchor, ChevronDown } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── PRICING DATA from official listino ───
const PRICING = {
  A: {
    label: { it: "Board & Sail A (Base)", en: "Board & Sail A (Base)" },
    color: "sky",
    options: [
      { key: "1h", label: "1 ora / 1 hour", price: 30 },
      { key: "2h", label: "2 ore / 2 hours", price: 40 },
      { key: "3h", label: "3 ore / 3 hours", price: 45 },
      { key: "4h", label: "4 ore / 4 hours", price: 55 },
      { key: "1day", label: "1 giorno / 1 day", price: 65 },
      { key: "2d", label: "2 giorni / 2 days", price: 95 },
      { key: "3d", label: "3 giorni / 3 days", price: 135 },
      { key: "4d", label: "4 giorni / 4 days", price: 175 },
      { key: "5d", label: "5 giorni / 5 days", price: 215 },
      { key: "6-7d", label: "6/7 giorni / days", price: 250 },
      { key: "12-14d", label: "12-14 giorni / days", price: 355 },
      { key: "18-21d", label: "18-21 giorni / days", price: 440 },
      { key: "4w", label: "4 settimane / 4 weeks", price: 550 },
      { key: "season", label: "Stagionale / Seasonal", price: 690 },
      { key: "5h_card", label: "Carta 5 ore / 5h Card", price: 130 },
      { key: "10h_card", label: "Carta 10 ore / 10h Card", price: 200 },
    ],
  },
  B: {
    label: { it: "Board & Sail B (Wing Surf)", en: "Board & Sail B (Wing Surf)" },
    color: "cyan",
    options: [
      { key: "1h", label: "1 ora / 1 hour", price: 35 },
      { key: "2h", label: "2 ore / 2 hours", price: 45 },
      { key: "3h", label: "3 ore / 3 hours", price: 55 },
      { key: "4h", label: "4 ore / 4 hours", price: 65 },
      { key: "1day", label: "1 giorno / 1 day", price: 75 },
      { key: "2d", label: "2 giorni / 2 days", price: 140 },
      { key: "3d", label: "3 giorni / 3 days", price: 170 },
      { key: "4d", label: "4 giorni / 4 days", price: 220 },
      { key: "5d", label: "5 giorni / 5 days", price: 240 },
      { key: "6-7d", label: "6/7 giorni / days", price: 310 },
      { key: "12-14d", label: "12-14 giorni / days", price: 410 },
      { key: "18-21d", label: "18-21 giorni / days", price: 540 },
      { key: "4w", label: "4 settimane / 4 weeks", price: 615 },
      { key: "season", label: "Stagionale / Seasonal", price: 950 },
      { key: "5h_card", label: "Carta 5 ore / 5h Card", price: 150 },
      { key: "10h_card", label: "Carta 10 ore / 10h Card", price: 280 },
    ],
  },
  GOLD: {
    label: { it: "PWA Equipment GOLD (Foil & Wing)", en: "PWA Equipment GOLD (Foil & Wing)" },
    color: "amber",
    options: [
      { key: "1h", label: "1 ora / 1 hour", price: 45 },
      { key: "2h", label: "2 ore / 2 hours", price: 65 },
      { key: "3h", label: "3 ore / 3 hours", price: 80 },
      { key: "4h", label: "4 ore / 4 hours", price: 95 },
      { key: "1day", label: "1 giorno / 1 day", price: 120 },
      { key: "2d", label: "2 giorni / 2 days", price: 170 },
      { key: "3d", label: "3 giorni / 3 days", price: 220 },
      { key: "4d", label: "4 giorni / 4 days", price: 275 },
      { key: "5d", label: "5 giorni / 5 days", price: 330 },
      { key: "6-7d", label: "6/7 giorni / days", price: 380 },
      { key: "12-14d", label: "12-14 giorni / days", price: 540 },
      { key: "18-21d", label: "18-21 giorni / days", price: 650 },
      { key: "4w", label: "4 settimane / 4 weeks", price: 750 },
      { key: "season", label: "Stagionale / Seasonal", price: 1000 },
      { key: "5h_card", label: "Carta 5 ore / 5h Card", price: 180 },
      { key: "10h_card", label: "Carta 10 ore / 10h Card", price: 330 },
    ],
  },
  C: {
    label: { it: "Board & Sail C (100% Carbonio)", en: "Board & Sail C (100% Carbon)" },
    color: "slate",
    options: [
      { key: "1h", label: "1 ora / 1 hour", price: 45 },
      { key: "2h", label: "2 ore / 2 hours", price: 65 },
      { key: "3h", label: "3 ore / 3 hours", price: 80 },
      { key: "4h", label: "4 ore / 4 hours", price: 95 },
      { key: "1day", label: "1 giorno / 1 day", price: 120 },
      { key: "2d", label: "2 giorni / 2 days", price: 170 },
      { key: "3d", label: "3 giorni / 3 days", price: 220 },
      { key: "4d", label: "4 giorni / 4 days", price: 275 },
      { key: "5d", label: "5 giorni / 5 days", price: 330 },
      { key: "6-7d", label: "6/7 giorni / days", price: 380 },
      { key: "12-14d", label: "12-14 giorni / days", price: 540 },
      { key: "18-21d", label: "18-21 giorni / days", price: 650 },
      { key: "4w", label: "4 settimane / 4 weeks", price: 750 },
      { key: "season", label: "Stagionale / Seasonal", price: 1000 },
      { key: "5h_card", label: "Carta 5 ore / 5h Card", price: 180 },
      { key: "10h_card", label: "Carta 10 ore / 10h Card", price: 330 },
    ],
  },
  D_BOARD: {
    label: { it: "Solo Tavola D (Tech/Wood/Carbon)", en: "Board Only D (Tech/Wood/Carbon)" },
    note: { it: "Foil Board +30%", en: "Foil Board +30%" },
    color: "violet",
    options: [
      { key: "1h", label: "1 ora / 1 hour", price: 20 },
      { key: "2h", label: "2 ore / 2 hours", price: 25 },
      { key: "3h", label: "3 ore / 3 hours", price: 30 },
      { key: "4h", label: "4 ore / 4 hours", price: 35 },
      { key: "1day", label: "1 giorno / 1 day", price: 45 },
      { key: "2d", label: "2 giorni / 2 days", price: 65 },
      { key: "3d", label: "3 giorni / 3 days", price: 85 },
      { key: "4d", label: "4 giorni / 4 days", price: 105 },
      { key: "5d", label: "5 giorni / 5 days", price: 125 },
      { key: "6-7d", label: "6/7 giorni / days", price: 145 },
      { key: "12-14d", label: "12-14 giorni / days", price: 240 },
      { key: "18-21d", label: "18-21 giorni / days", price: 290 },
      { key: "4w", label: "4 settimane / 4 weeks", price: 340 },
      { key: "season", label: "Stagionale / Seasonal", price: 480 },
      { key: "5h_card", label: "Carta 5 ore / 5h Card", price: 65 },
      { key: "10h_card", label: "Carta 10 ore / 10h Card", price: 125 },
    ],
  },
  D_SAIL: {
    label: { it: "Solo Vela D (Tech/Wood/Carbon)", en: "Sail Only D (Tech/Wood/Carbon)" },
    color: "purple",
    options: [
      { key: "1h", label: "1 ora / 1 hour", price: 25 },
      { key: "2h", label: "2 ore / 2 hours", price: 35 },
      { key: "3h", label: "3 ore / 3 hours", price: 45 },
      { key: "4h", label: "4 ore / 4 hours", price: 50 },
      { key: "1day", label: "1 giorno / 1 day", price: 60 },
      { key: "2d", label: "2 giorni / 2 days", price: 80 },
      { key: "3d", label: "3 giorni / 3 days", price: 100 },
      { key: "4d", label: "4 giorni / 4 days", price: 120 },
      { key: "5d", label: "5 giorni / 5 days", price: 155 },
      { key: "6-7d", label: "6/7 giorni / days", price: 190 },
      { key: "12-14d", label: "12-14 giorni / days", price: 300 },
      { key: "18-21d", label: "18-21 giorni / days", price: 340 },
      { key: "4w", label: "4 settimane / 4 weeks", price: 380 },
      { key: "season", label: "Stagionale / Seasonal", price: 570 },
      { key: "5h_card", label: "Carta 5 ore / 5h Card", price: 140 },
      { key: "10h_card", label: "Carta 10 ore / 10h Card", price: 190 },
    ],
  },
  SUP_KAYAK: {
    label: { it: "SUP / Kayak", en: "SUP / Kayak" },
    color: "blue",
    options: [
      { key: "1h", label: "1 ora / 1 hour", price: 20 },
      { key: "2h", label: "2 ore / 2 hours", price: 25 },
      { key: "3h", label: "3 ore / 3 hours", price: 30 },
      { key: "4h", label: "4 ore / 4 hours", price: 35 },
      { key: "1day", label: "1 giorno / 1 day", price: 50 },
      { key: "2d", label: "2 giorni / 2 days", price: 90 },
      { key: "3d", label: "3 giorni / 3 days", price: 120 },
      { key: "4d", label: "4 giorni / 4 days", price: 140 },
      { key: "5d", label: "5 giorni / 5 days", price: 175 },
      { key: "6-7d", label: "6/7 giorni / days", price: 200 },
    ],
    extra: { it: "Kayak doppio: +15€", en: "Double kayak: +15€" },
  },
  WETSUIT: {
    label: { it: "Muta / Wetsuit", en: "Wetsuit" },
    color: "orange",
    options: [
      { key: "1h", label: "1 ora / 1 hour", price: 7 },
      { key: "2h", label: "2 ore / 2 hours", price: 10 },
      { key: "3h", label: "3 ore / 3 hours", price: 10 },
      { key: "4h", label: "4 ore / 4 hours", price: 15 },
      { key: "1day", label: "1 giorno / 1 day", price: 20 },
      { key: "2d", label: "2 giorni / 2 days", price: 22 },
      { key: "3d", label: "3 giorni / 3 days", price: 24 },
      { key: "4d", label: "4 giorni / 4 days", price: 26 },
      { key: "5d", label: "5 giorni / 5 days", price: 28 },
      { key: "6-7d", label: "6/7 giorni / days", price: 30 },
    ],
    note: { it: "Con noleggio attrezzatura: +2€/h, +7€/giorno", en: "With equipment rental: +2€/h, +7€/day" },
  },
  HARNESS: {
    label: { it: "Trapezio / Harness", en: "Harness" },
    color: "rose",
    options: [
      { key: "1h", label: "1 ora / 1 hour", price: 7 },
      { key: "2h", label: "2 ore / 2 hours", price: 10 },
      { key: "3h", label: "3 ore / 3 hours", price: 10 },
      { key: "4h", label: "4 ore / 4 hours", price: 15 },
      { key: "1day", label: "1 giorno / 1 day", price: 20 },
      { key: "2d", label: "2 giorni / 2 days", price: 22 },
      { key: "3d", label: "3 giorni / 3 days", price: 24 },
      { key: "4d", label: "4 giorni / 4 days", price: 26 },
      { key: "5d", label: "5 giorni / 5 days", price: 28 },
      { key: "6-7d", label: "6/7 giorni / days", price: 30 },
    ],
    note: { it: "Con noleggio attrezzatura: +2€/h, +7€/giorno", en: "With equipment rental: +2€/h, +7€/day" },
  },
};

const translations = {
  it: {
    title: "Modulo Noleggio",
    subtitle: "Porto Pollo Academy A.S.D.",
    step1: "Dati Personali",
    step2: "Attrezzatura",
    step3: "Accessori",
    step4: "Orari & Firma",
    step5: "Norme di Noleggio",
    name: "Nome",
    surname: "Cognome",
    phone: "Telefono / WhatsApp",
    email: "Email",
    date: "Data",
    selectDuration: "Seleziona durata...",
    noSelection: "Nessuna selezione",
    equipmentDesc: "Tocca una categoria per selezionare durata e prezzo",
    accessories: "Accessori / Equipment",
    other: "Altro",
    quantity: "n.",
    startTime: "Ora di inizio / Start",
    endTime: "Ora di fine / Finish",
    session1: "Sessione 1",
    session2: "Sessione 2 (opzionale)",
    signatureLabel: "FIRMA per accettazione e presa visione delle NORME DI NOLEGGIO",
    signatureSubLabel: "SIGNATURE for acceptance and acknowledged of the RENTAL RULES",
    clearSignature: "Cancella firma",
    next: "Avanti",
    back: "Indietro",
    submit: "Invia Modulo",
    required: "Campo obbligatorio",
    success: "Modulo inviato con successo!",
    successDesc: "Il team ti aspetta al centro per il noleggio.",
    error: "Errore nell'invio del modulo",
    rulesTitle: "NORME DI NOLEGGIO",
    rulesSubtitle: "RENTAL RULES / VORSCHRIFTEN FUR DIE VERMIETUNG",
    rulesAttention: "ATTENZIONE: LEGGERE ACCURATAMENTE LE SEGUENTI NORME PRIMA DI NOLEGGIARE",
    acceptRules: "Dichiaro di aver letto e accettato le norme di noleggio",
    close: "Chiudi",
    footer: "IWD S.r.l, Via Spanu, 5 - 07100 Sassari Italia",
    viewRules: "Visualizza Norme di Noleggio",
    totalSummary: "Totale stimato",
    discountNote: "Sconti noleggio windsurf: -10% dopo 1a lezione, -15% dopo 3a lezione",
    lifeJacket: "Giubbotto salvagente GRATIS per tutti!",
    allIn: "Nolo ALL IN (Wind-Wing-Foil-Sup-Kayak): +20% su listino B",
    minRepair: "Costo minimo riparazione: 50€",
    idRequired: "Documento d'identita obbligatorio",
    memberQuestion: "Sei tesserato/a quest'anno?",
    memberYes: "Si, sono tesserato/a",
    memberNo: "No, non sono tesserato/a",
  },
  en: {
    title: "Rental Form",
    subtitle: "Porto Pollo Academy A.S.D.",
    step1: "Personal Info",
    step2: "Equipment",
    step3: "Accessories",
    step4: "Times & Signature",
    step5: "Rental Rules",
    name: "Name",
    surname: "Surname",
    phone: "Phone / WhatsApp",
    email: "Email",
    date: "Date",
    selectDuration: "Select duration...",
    noSelection: "No selection",
    equipmentDesc: "Tap a category to select duration and price",
    accessories: "Accessories / Equipment",
    other: "Other",
    quantity: "qty",
    startTime: "Start Time",
    endTime: "End Time",
    session1: "Session 1",
    session2: "Session 2 (optional)",
    signatureLabel: "SIGNATURE for acceptance and acknowledged of the RENTAL RULES",
    signatureSubLabel: "FIRMA per accettazione e presa visione delle NORME DI NOLEGGIO",
    clearSignature: "Clear signature",
    next: "Next",
    back: "Back",
    submit: "Submit Form",
    required: "Required field",
    success: "Form submitted successfully!",
    successDesc: "The team awaits you at the center for your rental.",
    error: "Error submitting form",
    rulesTitle: "RENTAL RULES",
    rulesSubtitle: "NORME DI NOLEGGIO / VORSCHRIFTEN FUR DIE VERMIETUNG",
    rulesAttention: "ATTENTION: Please read carefully the following regulation before hire!",
    acceptRules: "I declare that I have read and accepted the rental rules",
    close: "Close",
    footer: "IWD S.r.l, Via Spanu, 5 - 07100 Sassari Italia",
    viewRules: "View Rental Rules",
    totalSummary: "Estimated total",
    discountNote: "Windsurf rental discounts: -10% after 1st lesson, -15% after 3rd lesson",
    lifeJacket: "Life jacket FREE for everybody!",
    allIn: "ALL IN Rental (Wind-Wing-Foil-Sup-Kayak): +20% on price list B",
    minRepair: "Minimum repair cost: 50€",
    idRequired: "Mandatory identification required",
    memberQuestion: "Are you a member this year?",
    memberYes: "Yes, I'm a member",
    memberNo: "No, I'm not a member",
  },
  fr: {
    title: "Formulaire de Location",
    subtitle: "Porto Pollo Academy A.S.D.",
    step1: "Données Personnelles",
    step2: "Équipement",
    step3: "Accessoires",
    step4: "Horaires & Signature",
    step5: "Règles de Location",
    name: "Prénom",
    surname: "Nom",
    phone: "Telephone / WhatsApp",
    email: "Email",
    date: "Date",
    selectDuration: "Sélectionnez la durée...",
    noSelection: "Aucune sélection",
    equipmentDesc: "Touchez une catégorie pour sélectionner la durée et le prix",
    accessories: "Accessoires / Équipement",
    other: "Autre",
    quantity: "qté",
    startTime: "Heure de début / Start",
    endTime: "Heure de fin / Finish",
    session1: "Session 1",
    session2: "Session 2 (optionnelle)",
    signatureLabel: "SIGNATURE pour acceptation et prise de connaissance des RÈGLES DE LOCATION",
    signatureSubLabel: "FIRMA per accettazione e presa visione delle NORME DI NOLEGGIO",
    clearSignature: "Effacer la signature",
    next: "Suivant",
    back: "Retour",
    submit: "Envoyer",
    required: "Champ obligatoire",
    success: "Formulaire envoyé avec succès!",
    successDesc: "L'équipe vous attend au centre pour la location.",
    error: "Erreur lors de l'envoi",
    rulesTitle: "RÈGLES DE LOCATION",
    rulesSubtitle: "NORME DI NOLEGGIO / RENTAL RULES",
    rulesAttention: "ATTENTION: Veuillez lire attentivement les règles suivantes avant de louer!",
    acceptRules: "Je déclare avoir lu et accepté les règles de location",
    close: "Fermer",
    footer: "IWD S.r.l, Via Spanu, 5 - 07100 Sassari Italia",
    viewRules: "Voir les Règles de Location",
    totalSummary: "Total estimé",
    discountNote: "Réductions location windsurf: -10% après 1ère leçon, -15% après 3ème leçon",
    lifeJacket: "Gilet de sauvetage GRATUIT pour tous!",
    allIn: "Location ALL IN (Wind-Wing-Foil-Sup-Kayak): +20% sur liste B",
    minRepair: "Coût minimum de réparation: 50€",
    idRequired: "Pièce d'identité obligatoire",
    memberQuestion: "Êtes-vous membre cette année?",
    memberYes: "Oui, je suis membre",
    memberNo: "Non, je ne suis pas membre",
  },
  de: {
    title: "Mietformular",
    subtitle: "Porto Pollo Academy A.S.D.",
    step1: "Persönliche Daten",
    step2: "Ausrüstung",
    step3: "Zubehör",
    step4: "Zeiten & Unterschrift",
    step5: "Mietvorschriften",
    name: "Vorname",
    surname: "Nachname",
    phone: "Telefon / WhatsApp",
    email: "E-Mail",
    date: "Datum",
    selectDuration: "Dauer wählen...",
    noSelection: "Keine Auswahl",
    equipmentDesc: "Tippen Sie auf eine Kategorie um Dauer und Preis auszuwählen",
    accessories: "Zubehör / Ausrüstung",
    other: "Andere",
    quantity: "Anz.",
    startTime: "Startzeit / Start",
    endTime: "Endzeit / Finish",
    session1: "Session 1",
    session2: "Session 2 (optional)",
    signatureLabel: "UNTERSCHRIFT zur Annahme und Kenntnisnahme der MIETVORSCHRIFTEN",
    signatureSubLabel: "FIRMA per accettazione e presa visione delle NORME DI NOLEGGIO",
    clearSignature: "Unterschrift löschen",
    next: "Weiter",
    back: "Zurück",
    submit: "Absenden",
    required: "Pflichtfeld",
    success: "Formular erfolgreich gesendet!",
    successDesc: "Das Team erwartet Sie im Zentrum für die Vermietung.",
    error: "Fehler beim Senden",
    rulesTitle: "MIETVORSCHRIFTEN",
    rulesSubtitle: "NORME DI NOLEGGIO / RENTAL RULES",
    rulesAttention: "ACHTUNG: Bitte lesen Sie die folgenden Vorschriften sorgfältig durch!",
    acceptRules: "Ich erkläre, die Mietvorschriften gelesen und akzeptiert zu haben",
    close: "Schließen",
    footer: "IWD S.r.l, Via Spanu, 5 - 07100 Sassari Italia",
    viewRules: "Mietvorschriften anzeigen",
    totalSummary: "Geschätzter Gesamtbetrag",
    discountNote: "Windsurf-Mietrabatt: -10% nach 1. Stunde, -15% nach 3. Stunde",
    lifeJacket: "Schwimmweste GRATIS für alle!",
    allIn: "ALL IN Vermietung (Wind-Wing-Foil-Sup-Kayak): +20% auf Preisliste B",
    minRepair: "Mindestreparaturkosten: 50€",
    idRequired: "Ausweis erforderlich",
    memberQuestion: "Sind Sie dieses Jahr Mitglied?",
    memberYes: "Ja, ich bin Mitglied",
    memberNo: "Nein, ich bin kein Mitglied",
  },
  es: {
    title: "Formulario de Alquiler",
    subtitle: "Porto Pollo Academy A.S.D.",
    step1: "Datos Personales",
    step2: "Equipamiento",
    step3: "Accesorios",
    step4: "Horarios & Firma",
    step5: "Normas de Alquiler",
    name: "Nombre",
    surname: "Apellido",
    phone: "Telefono / WhatsApp",
    email: "Correo",
    date: "Fecha",
    selectDuration: "Seleccionar duración...",
    noSelection: "Sin selección",
    equipmentDesc: "Toca una categoría para seleccionar duración y precio",
    accessories: "Accesorios / Equipamiento",
    other: "Otro",
    quantity: "cant.",
    startTime: "Hora de inicio / Start",
    endTime: "Hora de fin / Finish",
    session1: "Sesión 1",
    session2: "Sesión 2 (opcional)",
    signatureLabel: "FIRMA para aceptación y conocimiento de las NORMAS DE ALQUILER",
    signatureSubLabel: "FIRMA per accettazione e presa visione delle NORME DI NOLEGGIO",
    clearSignature: "Borrar firma",
    next: "Siguiente",
    back: "Atrás",
    submit: "Enviar Formulario",
    required: "Campo obligatorio",
    success: "¡Formulario enviado con éxito!",
    successDesc: "El equipo te espera en el centro para el alquiler.",
    error: "Error al enviar",
    rulesTitle: "NORMAS DE ALQUILER",
    rulesSubtitle: "NORME DI NOLEGGIO / RENTAL RULES",
    rulesAttention: "¡ATENCIÓN: Lea atentamente las siguientes normas antes de alquilar!",
    acceptRules: "Declaro haber leído y aceptado las normas de alquiler",
    close: "Cerrar",
    footer: "IWD S.r.l, Via Spanu, 5 - 07100 Sassari Italia",
    viewRules: "Ver Normas de Alquiler",
    totalSummary: "Total estimado",
    discountNote: "Descuentos alquiler windsurf: -10% después de 1ª lección, -15% después de 3ª lección",
    lifeJacket: "¡Chaleco salvavidas GRATIS para todos!",
    allIn: "Alquiler ALL IN (Wind-Wing-Foil-Sup-Kayak): +20% sobre lista B",
    minRepair: "Coste mínimo de reparación: 50€",
    idRequired: "Documento de identidad obligatorio",
    memberQuestion: "¿Eres miembro este año?",
    memberYes: "Sí, soy miembro",
    memberNo: "No, no soy miembro",
  },
};

const rentalRules = [
  { num: "1", it: "CHI NOLEGGIA DEVE TRATTARE IL MATERIALE CON CURA.", en: "Rental equipment must be treated with care.", de: "Wer Material anmietet muss es sorgfaltig behandeln." },
  { num: "2", it: "CONTROLLATE SEMPRE LO STATO DEL MATERIALE INSIEME AL PERSONALE PRIMA DI USCIRE. VA RESTITUITO NELLO STESSO STATO IN CUI VI E' STATO CONSEGNATO.", en: "Before your departure check with assigned personnel. It must be returned in the same condition it was given to you.", de: "Bevor Sie starten kontrollieren Sie immer den Zustand der Ausrustung mit dem Personal. Es muss im selben Zustand sein wie wir es Ihnen gegeben haben." },
  { num: "3", it: "EVENTUALI DANNI VERRANNO VALUTATI COME SEGUE:\n- VELE/TAVOLE: IL COSTO DELLA RIPARAZIONE VA PAGATO INTERAMENTE\n- DANNO DA SVALUTAZIONE AMMACCATURE SU TAVOLE, VELE O BOMA: EUR 30,00\n- SCHEGGIATURA PINNA: EUR 25,00", en: "Any possible damages will be evaluated as follows:\n- Sails/boards: cost of repair\n- Slight scratches on boards, sails or boom: EUR 30.00\n- Splintering on the fin: EUR 25.00", de: "Eventuelle auftretende Schaden werden wie folgt berechnet:\n- Segel/Board: vollstandige Reparationspreis\n- Kleine Beschadigungen am Board, Segel oder Gabelbaum: EUR 30,00\n- Beschadigung der Finne: EUR 25,00" },
  { num: "4", it: "NAVIGARE SEMPRE IN MODO DA ESSERE VISTI DAL PERSONALE ADDETTO.", en: "Make sure you sail where personnel in charge may always see you.", de: "Fahren Sie immer dort, wo Sie vom zustandigen Personal gesehen werden konnen." },
  { num: "5", it: "PRESTARE ATTENZIONE AGLI ALTRI SURFISTI E RISPETTARE LE NORME DI SICUREZZA.", en: "Watch out for the other windsurfers and respect the rights of way.", de: "Achten Sie auf die anderen Windsurfer und beachten Sie die Vorfahrtsregeln." },
  { num: "6", it: "IN CASO DI RECUPERO VERRA' ADDEBITATO L'IMPORTO DI EUR 25,00.", en: "EUR 25.00 will be charged in case of rescue.", de: "Im Falle einer Rettungsaktion mussen Sie EUR 25,00 bezahlen." },
  { num: "7", it: "E' OBBLIGATORIO L'UTILIZZO DEL SALVAGENTE.", en: "Wearing the life jacket is compulsory.", de: "Die Verwendung der Schwimmweste ist Pflicht." },
  { num: "8", it: "DURANTE LE PAUSE IN SPIAGGIA SIETE PREGATI DI RIPORTARE IL MATERIALE ALL'OMBRA E COMUNQUE DI NON LASCIARLO SUL BAGNASCIUGA (LA LUCE SOLARE NUOCE ALL'ATTREZZATURA).", en: "Never leave the equipment on the waterline when you are not windsurfing but bring it back in the shadow (the sun damages the equipment).", de: "Bei Unterbrechungen bitte die Ausrustung nicht alleine am Strand sondern im Schatten liegen lassen (das Sonnenlicht schadet der Ausrustung)." },
  { num: "9", it: "OGNI FRAZIONE DI ORA SARA' CONTEGGIATA COME INTERA.", en: "Fraction of hours will be considered as entire hour.", de: "Jede angefangene Stunde wird als volle Stunde berechnet." },
];

// Color mappings for Tailwind classes
const colorMap = {
  sky: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700", badge: "bg-sky-100 text-sky-700", ring: "ring-sky-300" },
  cyan: { bg: "bg-cyan-50", border: "border-cyan-200", text: "text-cyan-700", badge: "bg-cyan-100 text-cyan-700", ring: "ring-cyan-300" },
  amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", badge: "bg-amber-100 text-amber-800", ring: "ring-amber-300" },
  slate: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", badge: "bg-slate-200 text-slate-700", ring: "ring-slate-300" },
  violet: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", badge: "bg-violet-100 text-violet-700", ring: "ring-violet-300" },
  purple: { bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-700", badge: "bg-purple-100 text-purple-700", ring: "ring-purple-300" },
  blue: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", badge: "bg-blue-100 text-blue-700", ring: "ring-blue-300" },
  orange: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-700", badge: "bg-orange-100 text-orange-700", ring: "ring-orange-300" },
  rose: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", badge: "bg-rose-100 text-rose-700", ring: "ring-rose-300" },
};

// ─── PRICING DROPDOWN COMPONENT ───
const PricingDropdown = ({ catKey, cat, selected, onSelect, lang }) => {
  const [open, setOpen] = useState(false);
  const c = colorMap[cat.color] || colorMap.sky;
  const langKey = lang === "en" ? "en" : "it";
  const selectedOpt = selected ? cat.options.find(o => o.key === selected) : null;

  return (
    <div className={`rounded-xl border ${c.border} overflow-hidden transition-all ${selected ? c.bg : "bg-white"}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={`rental-cat-${catKey}`}
        className={`w-full flex items-center justify-between px-3.5 py-3 text-left transition-colors ${open ? c.bg : "hover:bg-slate-50"}`}
      >
        <div className="flex-1 min-w-0">
          <span className={`text-sm font-semibold ${c.text}`}>{cat.label[langKey]}</span>
          {cat.note && <span className={`block text-[10px] ${c.text} opacity-60`}>{cat.note[langKey]}</span>}
          {selectedOpt && (
            <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.badge}`}>
              {selectedOpt.label} — {selectedOpt.price}€
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
          {selectedOpt && (
            <span className="text-base font-bold text-slate-800">{selectedOpt.price}€</span>
          )}
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-2 pb-2 max-h-48 overflow-y-auto">
              {/* No selection option */}
              <button
                type="button"
                onClick={() => { onSelect(catKey, ""); setOpen(false); }}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors mb-0.5 ${!selected ? "bg-slate-100 font-medium" : "hover:bg-slate-50 text-slate-400"}`}
              >
                — {lang === "en" ? "No selection" : "Nessuna selezione"}
              </button>
              {cat.options.map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  data-testid={`rental-opt-${catKey}-${opt.key}`}
                  onClick={() => { onSelect(catKey, opt.key); setOpen(false); }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors mb-0.5 flex items-center justify-between ${selected === opt.key ? `${c.bg} font-semibold ${c.text} ring-1 ${c.ring}` : "hover:bg-slate-50"}`}
                >
                  <span>{opt.label}</span>
                  <span className={`font-bold ${selected === opt.key ? c.text : "text-slate-700"}`}>{opt.price}€</span>
                </button>
              ))}
            </div>
            {cat.extra && (
              <p className={`text-[10px] px-3 pb-2 ${c.text} opacity-60`}>{cat.extra[langKey]}</p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const StorageForm = ({ isOpen, onClose, language = "it", sessionId = "" }) => {
  const t = translations[language] || translations.it;
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [formData, setFormData] = useState({
    name: "", surname: "", phone: "", email: "", date: new Date().toISOString().split("T")[0],
    is_member: null,
    // Equipment selections (store the duration key)
    rental_A: "", rental_B: "", rental_GOLD: "", rental_C: "",
    rental_D_BOARD: "", rental_D_SAIL: "", rental_SUP_KAYAK: "",
    // Accessories
    rental_WETSUIT: "", rental_HARNESS: "",
    accessory_other: "", accessory_other_count: 0,
    // Times
    start_time_1: "", end_time_1: "",
    start_time_2: "", end_time_2: "",
    signature_data: "", accepts_rental_rules: false,
  });

  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRentalSelect = (catKey, durKey) => {
    updateField(`rental_${catKey}`, durKey);
  };

  // Calculate total from selections
  const calcTotal = () => {
    let total = 0;
    Object.entries(PRICING).forEach(([catKey, cat]) => {
      const sel = formData[`rental_${catKey}`];
      if (sel) {
        const opt = cat.options.find(o => o.key === sel);
        if (opt) total += opt.price;
      }
    });
    return total;
  };

  const getCanvasCoords = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDraw = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    isDrawing.current = true;
    lastPos.current = getCanvasCoords(e, canvas);
  }, []);

  const draw = useCallback((e) => {
    e.preventDefault();
    if (!isDrawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getCanvasCoords(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "#1a1a2e";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.stroke();
    lastPos.current = pos;
  }, []);

  const endDraw = useCallback(() => {
    isDrawing.current = false;
    const canvas = canvasRef.current;
    if (canvas) updateField("signature_data", canvas.toDataURL("image/png"));
  }, []);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      updateField("signature_data", "");
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || step !== 4) return;
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);
  }, [step]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError("");
    try {
      // Build equipment array for backend
      const equipment = [];
      Object.entries(PRICING).forEach(([catKey, cat]) => {
        const sel = formData[`rental_${catKey}`];
        if (sel) {
          const opt = cat.options.find(o => o.key === sel);
          if (opt) {
            equipment.push({
              type: cat.label.it,
              duration: opt.label,
              cost: opt.price,
            });
          }
        }
      });

      const payload = {
        name: formData.name,
        surname: formData.surname,
        phone: formData.phone,
        email: formData.email,
        date: formData.date,
        is_member: formData.is_member === true,
        equipment,
        total_cost: calcTotal(),
        accessory_other: formData.accessory_other,
        accessory_other_count: formData.accessory_other_count,
        start_time_1: formData.start_time_1,
        end_time_1: formData.end_time_1,
        start_time_2: formData.start_time_2,
        end_time_2: formData.end_time_2,
        signature_data: formData.signature_data,
        accepts_rental_rules: formData.accepts_rental_rules,
        session_id: sessionId,
      };

      const res = await fetch(`${API_URL}/api/storage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Server error");
      setSubmitSuccess(true);
    } catch (err) {
      setSubmitError(t.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canGoNext = () => {
    if (step === 1) return formData.name && formData.surname && formData.phone;
    if (step === 4) return formData.signature_data && formData.accepts_rental_rules;
    return true;
  };

  const totalSteps = 5;
  const total = calcTotal();

  const InputField = ({ label, field, type = "text", placeholder = "" }) => (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</label>
      <input
        data-testid={`storage-${field}`}
        type={type}
        value={formData[field]}
        onChange={(e) => updateField(field, e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white/80 border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-400 focus:border-transparent outline-none transition-all"
      />
    </div>
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          className="relative w-full sm:max-w-lg max-h-[92vh] bg-gradient-to-b from-slate-50 to-white rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-sky-600 to-cyan-500 px-5 py-4 text-white flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Anchor className="w-5 h-5" />
                <div>
                  <h2 className="font-bold text-lg" data-testid="storage-form-title">{t.title}</h2>
                  <p className="text-xs text-white/80">{t.subtitle}</p>
                </div>
              </div>
              <button onClick={onClose} data-testid="storage-form-close" className="p-1.5 rounded-full hover:bg-white/20 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex gap-1 mt-3">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i < step ? "bg-white" : "bg-white/30"}`} />
              ))}
            </div>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-white/70">{step <= totalSteps && t[`step${step}`]} ({step}/{totalSteps})</p>
              {total > 0 && step <= 3 && (
                <p className="text-xs font-bold bg-white/20 px-2 py-0.5 rounded-full" data-testid="rental-total-badge">
                  {t.totalSummary}: {total}€
                </p>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {submitSuccess ? (
              <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center py-8 space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800">{t.success}</h3>
                <p className="text-slate-500 text-sm">{t.successDesc}</p>
                <button onClick={onClose} data-testid="storage-form-close-success" className="mt-4 px-6 py-2.5 bg-sky-500 text-white rounded-full font-medium hover:bg-sky-600 transition-colors">
                  {t.close}
                </button>
              </motion.div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ x: 30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -30, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-3"
                >
                  {/* Step 1: Personal Info */}
                  {step === 1 && (
                    <>
                      {/* Membership question */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-bold text-slate-600">{t.memberQuestion}</h4>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            data-testid="rental-member-yes"
                            onClick={() => updateField("is_member", true)}
                            className={`p-3 rounded-xl border-2 text-center text-sm font-semibold transition-all ${formData.is_member === true ? "border-emerald-500 bg-emerald-50 text-emerald-700 ring-2 ring-emerald-200" : "border-slate-200 hover:border-slate-300 text-slate-600"}`}
                          >
                            {t.memberYes}
                          </button>
                          <button
                            type="button"
                            data-testid="rental-member-no"
                            onClick={() => updateField("is_member", false)}
                            className={`p-3 rounded-xl border-2 text-center text-sm font-semibold transition-all ${formData.is_member === false ? "border-orange-500 bg-orange-50 text-orange-700 ring-2 ring-orange-200" : "border-slate-200 hover:border-slate-300 text-slate-600"}`}
                          >
                            {t.memberNo}
                          </button>
                        </div>
                      </div>
                      <InputField label={t.name + " *"} field="name" placeholder="Mario" />
                      <InputField label={t.surname + " *"} field="surname" placeholder="Rossi" />
                      <InputField label={t.phone + " *"} field="phone" type="tel" placeholder="+39 351..." />
                      <InputField label={t.email} field="email" type="email" placeholder="mario@email.com" />
                      <InputField label={t.date} field="date" type="date" />
                      {/* Info cards */}
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                          <span className="text-green-600 text-sm font-medium">{t.lifeJacket}</span>
                        </div>
                        <div className="flex items-center gap-2 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">
                          <span className="text-sky-600 text-xs">{t.discountNote}</span>
                        </div>
                        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                          <span className="text-slate-500 text-xs">{t.idRequired} | {t.minRepair}</span>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Step 2: Equipment with pricing dropdowns */}
                  {step === 2 && (
                    <>
                      <p className="text-xs text-slate-400 mb-1">{t.equipmentDesc}</p>
                      {["A", "B", "GOLD", "C", "D_BOARD", "D_SAIL", "SUP_KAYAK"].map(catKey => (
                        <PricingDropdown
                          key={catKey}
                          catKey={catKey}
                          cat={PRICING[catKey]}
                          selected={formData[`rental_${catKey}`]}
                          onSelect={handleRentalSelect}
                          lang={language}
                        />
                      ))}
                      <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        <p className="text-[10px] text-amber-700">{t.allIn}</p>
                      </div>
                      {total > 0 && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-center" data-testid="rental-total-step2">
                          <p className="text-xs text-emerald-600 font-medium">{t.totalSummary}</p>
                          <p className="text-2xl font-bold text-emerald-700">{total}€</p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Step 3: Accessories */}
                  {step === 3 && (
                    <>
                      <PricingDropdown
                        catKey="WETSUIT"
                        cat={PRICING.WETSUIT}
                        selected={formData.rental_WETSUIT}
                        onSelect={handleRentalSelect}
                        lang={language}
                      />
                      <PricingDropdown
                        catKey="HARNESS"
                        cat={PRICING.HARNESS}
                        selected={formData.rental_HARNESS}
                        onSelect={handleRentalSelect}
                        lang={language}
                      />
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
                        <h4 className="font-bold text-sm text-slate-600">{t.other}</h4>
                        <div className="grid grid-cols-2 gap-2">
                          <InputField label={t.other} field="accessory_other" placeholder="Stivaletti..." />
                          <div className="space-y-1">
                            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t.quantity}</label>
                            <input
                              type="number"
                              min="0"
                              value={formData.accessory_other_count}
                              onChange={(e) => updateField("accessory_other_count", parseInt(e.target.value) || 0)}
                              className="w-full bg-white/80 border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-400 focus:border-transparent outline-none"
                            />
                          </div>
                        </div>
                      </div>
                      {total > 0 && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-center" data-testid="rental-total-step3">
                          <p className="text-xs text-emerald-600 font-medium">{t.totalSummary}</p>
                          <p className="text-2xl font-bold text-emerald-700">{total}€</p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Step 4: Times & Signature */}
                  {step === 4 && (
                    <>
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-3">
                        <h4 className="font-bold text-sm text-slate-600">{t.session1}</h4>
                        <div className="grid grid-cols-2 gap-2">
                          <InputField label={t.startTime} field="start_time_1" type="time" />
                          <InputField label={t.endTime} field="end_time_1" type="time" />
                        </div>
                      </div>
                      <div className="bg-slate-50/50 border border-slate-100 rounded-xl p-3 space-y-3">
                        <h4 className="font-bold text-sm text-slate-400">{t.session2}</h4>
                        <div className="grid grid-cols-2 gap-2">
                          <InputField label={t.startTime} field="start_time_2" type="time" />
                          <InputField label={t.endTime} field="end_time_2" type="time" />
                        </div>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                        <button type="button" onClick={() => setStep(5)} className="text-sm text-amber-700 underline font-medium hover:text-amber-800" data-testid="storage-view-rules">
                          {t.viewRules}
                        </button>
                        <label className="flex items-start gap-2 mt-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.accepts_rental_rules}
                            onChange={(e) => updateField("accepts_rental_rules", e.target.checked)}
                            className="mt-1 w-4 h-4 rounded border-slate-300 text-sky-500 focus:ring-sky-400"
                            data-testid="storage-accept-rules"
                          />
                          <span className="text-xs text-slate-600">{t.acceptRules} *</span>
                        </label>
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t.signatureLabel} *</p>
                        <p className="text-[10px] text-slate-400">{t.signatureSubLabel}</p>
                        <div className="relative border-2 border-dashed border-slate-300 rounded-xl overflow-hidden bg-white" style={{ height: "120px" }}>
                          <canvas
                            ref={canvasRef}
                            data-testid="storage-signature-canvas"
                            className="w-full h-full touch-none"
                            onMouseDown={startDraw}
                            onMouseMove={draw}
                            onMouseUp={endDraw}
                            onMouseLeave={endDraw}
                            onTouchStart={startDraw}
                            onTouchMove={draw}
                            onTouchEnd={endDraw}
                          />
                        </div>
                        <button type="button" onClick={clearSignature} className="text-xs text-red-500 hover:text-red-600">
                          {t.clearSignature}
                        </button>
                      </div>
                    </>
                  )}

                  {/* Step 5: Rental Rules */}
                  {step === 5 && (
                    <div className="space-y-4">
                      <div className="text-center space-y-1">
                        <h3 className="font-bold text-base text-slate-800">{t.rulesTitle}</h3>
                        <p className="text-xs text-slate-400">{t.rulesSubtitle}</p>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 text-center">
                        <p className="text-xs font-bold text-red-600">{t.rulesAttention}</p>
                      </div>
                      <div className="space-y-3">
                        {rentalRules.map((rule) => (
                          <div key={rule.num} className="bg-white border border-slate-100 rounded-lg p-3">
                            <p className="text-sm font-semibold text-slate-800 whitespace-pre-line">{rule.num}. {rule.it}</p>
                            <p className="text-xs text-slate-500 mt-1 italic whitespace-pre-line">{rule.en}</p>
                            <p className="text-[10px] text-slate-400 mt-0.5 italic whitespace-pre-line">{rule.de}</p>
                          </div>
                        ))}
                      </div>
                      <div className="text-center pt-2 border-t border-slate-200">
                        <p className="text-[10px] text-slate-400">{t.footer}</p>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            )}

            {submitError && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-600">{submitError}</p>
              </div>
            )}
          </div>

          {/* Footer Buttons */}
          {!submitSuccess && (
            <div className="flex-shrink-0 px-5 py-3 bg-white border-t border-slate-100 flex items-center justify-between gap-3">
              {step > 1 ? (
                <button
                  onClick={() => setStep(step === 5 ? 4 : step - 1)}
                  data-testid="storage-form-back"
                  className="flex items-center gap-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" /> {t.back}
                </button>
              ) : <div />}

              {step < 4 ? (
                <button
                  onClick={() => setStep(step + 1)}
                  disabled={!canGoNext()}
                  data-testid="storage-form-next"
                  className="flex items-center gap-1 px-5 py-2.5 text-sm font-medium text-white bg-sky-500 rounded-full hover:bg-sky-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {t.next} <ChevronRight className="w-4 h-4" />
                </button>
              ) : step === 4 ? (
                <button
                  onClick={handleSubmit}
                  disabled={!canGoNext() || isSubmitting}
                  data-testid="storage-form-submit"
                  className="flex items-center gap-1 px-5 py-2.5 text-sm font-medium text-white bg-green-500 rounded-full hover:bg-green-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? "..." : t.submit} <Check className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={() => setStep(4)}
                  data-testid="storage-form-back-to-sign"
                  className="flex items-center gap-1 px-5 py-2.5 text-sm font-medium text-white bg-sky-500 rounded-full hover:bg-sky-600 transition-colors"
                >
                  {t.back} <ChevronLeft className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default StorageForm;
